import { useMutation } from "@apollo/react-hooks";
import { createProduct as createProductMutation } from "../mutations";
import { parseCurrency, trim } from "../../../helpers/StringManipulation";
import { GetOwnedProducts } from "../../../pages/Products/queries";
import { buildGetOwnedProductsVariables } from "../helpers";
import { GetProducts } from "../../../pages/Videos/queries";
import { getOwnedProducts } from "../../OpportunitySidebar/queries";

const useCreateProduct = ({
  product,
  searchText,
  informationTabProps: {
    hasVariants,
    acceptsDonations,
    ...informationTabProps
  },
  variantTabProps,
  imageTabProps
}) => {
  const variables = !product && {
    product: buildAddProductVariable(
      informationTabProps,
      hasVariants,
      acceptsDonations
    ),
    variants: buildAddVariantsVariable(variantTabProps, hasVariants),
    options: buildAddOptionsVariable(variantTabProps),
    images: buildAddImagesVariable(informationTabProps, imageTabProps)
  };

  return useMutation(createProductMutation, {
    variables,
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetOwnedProducts,
        ...buildGetOwnedProductsVariables(searchText)
      },
      { query: GetProducts },
      { query: getOwnedProducts }
    ]
  });
};

const buildAddProductVariable = (
  { name, description, selectedCategories, price, stock, sku },
  hasVariants,
  acceptsDonations
) => ({
  name,
  description: description.trim(),
  categories: selectedCategories.map(({ id }) => id),
  ...(!hasVariants && !acceptsDonations && { price: parseCurrency(price) }),
  ...(!hasVariants && { stock: parseInt(stock) }),
  ...(!hasVariants && { sku: sku.trim() }),
  isDonation: acceptsDonations
});

const buildAddOptionsVariable = ({ columns }) =>
  columns
    .filter(({ stateKey }) => stateKey.includes("variantOption"))
    .map(({ title }) => trim(title));

const buildAddImagesVariable = (informationTabProps, imageTabProps) => [
  { url: informationTabProps.productImage, order: 0 },
  ...imageTabProps.images.map((image) => ({ ...image, order: image.order + 1 }))
];

export const buildAddVariantsVariable = (variantTabProps, hasVariants) =>
  !hasVariants
    ? null
    : variantTabProps.rows.map(({ id, price, stock, sku, ...row }) => ({
        id,
        price: parseCurrency(price),
        stock: parseInt(stock),
        sku: sku.trim(),
        selectedValues: buildSelectedValues(row, variantTabProps.columns)
      }));

export const buildSelectedValues = (variantValues, columns) =>
  Object.keys(variantValues).map((variantValueKey) => {
    const matchingOption = columns.find(
      (column) => column.stateKey === variantValueKey
    );
    return {
      valueName: variantValues[variantValueKey].trim(),
      optionName: matchingOption.title && matchingOption.title.trim()
    };
  });

export default useCreateProduct;
